import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Tooltip } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ConfirmationHelperService } from '../../services/confirmation-helper.service';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { StoreInterfaceService } from '../../services/store-interface.service';
import { TestModeService } from '../../services/test-mode.service';
import { ChoiceListElementStore } from '../../stores/choice-list-element.store';
import { ChoiceListElement } from '../../types/choice-list-element.interface';
import { ChoiceList } from '../../types/choice-list.interface';
import { BusinessRule, BusinessRuleType, EntityDefinition, FieldConfig } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { buildDeleteConfig } from '../../utils/delete-confirmation';
import { CesDeleteButtonComponent } from '../ui/ces-buttons';
import { CesDialogFooterComponent } from '../ui/dialog-footer/dialog-footer.component';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';
import { ChoiceListElementsEditorComponent } from './choice-list-elements-editor.component';

import { CesTooltipAutoDismissDirective } from '../../directives/ces-tooltip-auto-dismiss.directive';
type ChoiceListRule = BusinessRule<ChoiceList>;

interface RenameInfo {
  element: Partial<ChoiceListElement>;
  oldName: string;
  newName: string;
}

type ChoiceListFormSubmitPayload = EntityFormValue<ChoiceList> & {
  __elements: Partial<ChoiceListElement>[];
};

@Component({
  selector: 'app-choice-list-form',
  standalone: true,
  imports: [
    CesTooltipAutoDismissDirective,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    ConfirmPopupModule,
    ChoiceListElementsEditorComponent,
    CesTextFieldComponent,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    Tooltip,
    TranslocoModule,
  ],
  templateUrl: './choice-list-form.component.html',
})
export class ChoiceListFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() entityDefinition?: EntityDefinition<ChoiceList>;
  @Input() entity?: ChoiceList;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<ChoiceListFormSubmitPayload>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<ChoiceList>();

  @ViewChild('elementsEditor') elementsEditor?: ChoiceListElementsEditorComponent;

  form: FormGroup = new FormGroup({});
  elements: Partial<ChoiceListElement>[] = [];
  originalElements: ChoiceListElement[] = [];
  errorMessage = '';
  submitting = false;
  formSubmitted = false;
  renamedElements: RenameInfo[] = [];
  elementsValid = true;

  protected externalErrors: Record<string, Record<string, string>> = {};
  private formValueSubscription?: Subscription;
  private initialValidationDone = false;
  private validationFields: string[] = [];

  private readonly dialogConfig = inject<DynamicDialogConfig<EntityDialogData<ChoiceList>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<ChoiceListFormComponent> | null>(DynamicDialogRef, { optional: true });
  private readonly choiceListElementStore = inject(ChoiceListElementStore);
  private readonly fb = inject(FormBuilder);
  private readonly storeService = inject(StoreInterfaceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  protected readonly t = inject(TestModeService);
  private readonly translateService = inject(TranslocoService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);

  ngOnInit(): void {
    const data = this.dialogConfig?.data;
    if (data) {
      this.entityDefinition = data.entityDefinition;
      this.mode = data.mode === 'edit' ? 'edit' : 'create';
      if (data.entity !== undefined) this.entity = data.entity;
      if (data.parentId !== undefined) this.parentId = data.parentId;
      if (data.additionalData !== undefined) this.additionalData = data.additionalData;
    }

    if (!this.entityDefinition) {
      throw new Error('Entity definition is required');
    }

    this.initForm();

    if (this.mode === 'edit' && this.entity) {
      void this.loadExistingElements(this.entity.id);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.validateBusinessRules();
      this.subscribeToFormChanges();
      this.initialValidationDone = true;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.formValueSubscription) {
      this.formValueSubscription.unsubscribe();
    }
  }

  private subscribeToFormChanges(): void {
    this.formValueSubscription = this.form.valueChanges.subscribe(() => {
      this.zone.run(() => {
        this.validateBusinessRules();
      });
    });
  }

  private validateBusinessRules(): void {
    const validationRules = this.entityDefinition?.validationRules;
    if (!validationRules || !this.storeService) {
      return;
    }

    const validationType = this.mode === 'create' ? BusinessRuleType.CREATE_VALIDATION : BusinessRuleType.EDIT_VALIDATION;
    const rules = validationRules.filter(rule => rule.type === validationType);
    if (rules.length === 0) {
      return;
    }

    const newExternalErrors: Record<string, Record<string, string>> = {};
    this.validationFields.forEach(fieldName => {
      newExternalErrors[fieldName] = {};
    });

    this.validationFields.forEach(fieldName => {
      const control = this.form.get(fieldName);
      if (!control) return;

      const formValue: unknown = this.form.value;
      const entityToValidate = {
        ...(formValue as Partial<ChoiceList>),
        ...(this.entity ? { id: this.entity.id } : { id: undefined }),
      } as unknown as ChoiceList;

      for (const rule of rules) {
        try {
          if (!rule.rule(entityToValidate, this.storeService)) {
            const errorMsg = rule.errorMessageKey ? this.translateService.translate(rule.errorMessageKey) : (rule.errorMessage ?? '');
            newExternalErrors[fieldName] = {
              ...newExternalErrors[fieldName],
              businessRule: errorMsg,
            };
            break;
          }
        } catch (error) {
          console.error(this.translateService.translate('common.errors.businessRuleValidationError'), error);
        }
      }
    });

    this.externalErrors = newExternalErrors;

    if (this.initialValidationDone) {
      this.cdr.detectChanges();
    }
  }

  fieldConfig(field: string): FieldConfig | undefined {
    return this.entityDefinition?.fields?.find(f => f.field === field);
  }

  getExternalErrorsForField(fieldName: string): Record<string, string> {
    return this.externalErrors[fieldName] ?? {};
  }

  private initForm(): void {
    const formConfig: Record<string, [unknown, ValidatorFn[]]> = {};

    if (this.entityDefinition?.fields) {
      for (const fieldConfig of this.entityDefinition.fields) {
        const validators: ValidatorFn[] = [];

        let defaultValue: unknown = '';

        const entityValue = this.entity?.[fieldConfig.field as keyof typeof this.entity];
        if (entityValue !== undefined) {
          defaultValue = entityValue;
        } else if (fieldConfig.defaultValue !== undefined) {
          defaultValue = fieldConfig.defaultValue;
        }

        formConfig[fieldConfig.field] = [defaultValue, validators];
      }
    }

    this.form = this.fb.group(formConfig);

    this.validationFields = this.entityDefinition?.validationFields ?? ['name'];
  }

  private detectRenamedElements(): void {
    this.renamedElements = [];

    if (!this.isListUsed() || !this.entity) {
      return;
    }

    for (const element of this.elements) {
      if (element.id) {
        const originalElement = this.originalElements.find(orig => orig.id === element.id);

        if (originalElement && element.name?.trim() !== originalElement.name.trim()) {
          this.renamedElements.push({
            element,
            oldName: originalElement.name,
            newName: element.name?.trim() ?? '',
          });
        }
      }
    }
  }

  private async loadExistingElements(listId: number): Promise<void> {
    try {
      const store = this.choiceListElementStore as unknown as { getElementsForList(listId: number): ChoiceListElement[] };
      this.originalElements = store.getElementsForList(listId);
      this.elements = this.originalElements.map(element => ({ ...element }));
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    }
  }

  onElementsChange(updatedElements: Partial<ChoiceListElement>[]): void {
    this.elements = updatedElements;
    this.detectRenamedElements();
  }

  onElementsValidityChange(valid: boolean): void {
    this.elementsValid = valid;
  }

  isCreateMode(): boolean {
    return this.mode === 'create';
  }

  isListUsed(): boolean {
    return !!this.entity?.isUsed;
  }

  hasRenamedElements(): boolean {
    return this.isListUsed() && this.renamedElements.length > 0;
  }

  isFormValid(): boolean {
    if (this.form.invalid) {
      return false;
    }

    if (!this.elementsValid) {
      return false;
    }

    return true;
  }

  isFormInvalid(): boolean {
    return this.form.invalid || this.submitting || !this.isFormValid() || this.hasBusinessRuleErrors();
  }

  private hasBusinessRuleErrors(): boolean {
    return Object.values(this.externalErrors).some(fieldErrors => Object.keys(fieldErrors).length > 0);
  }

  async processSubmit(): Promise<void> {
    // Renaming choices on a list already in use warrants an explicit confirmation.
    if (this.hasRenamedElements()) {
      const confirmed = await this.confirmationHelper.ask(
        'entities.choiceList.messages.renameWarning',
        'common.dialogs.confirm',
        undefined,
        'pi pi-exclamation-triangle',
      );
      if (!confirmed) return;
    }

    this.submitting = true;
    this.errorMessage = '';

    const formValue = this.form.value as Partial<ChoiceList>;
    const formData: ChoiceListFormSubmitPayload = {
      ...formValue,
      __elements: this.elements.filter(e => e.name?.trim()),
    };

    if (this.dialogRef) {
      this.dialogRef.close(formData);
    } else {
      this.formSubmit.emit(formData);
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  canShowDeleteButton(): boolean {
    return this.entityDefinition?.formOptions?.showDeleteButton === true && this.entity !== undefined;
  }

  canDeleteEntity(): boolean {
    const entity = this.entity;
    if (!entity) return false;

    const deleteRules = this.getDeleteRules();
    if (deleteRules.length === 0) return true;

    return deleteRules.every(rule => rule.rule(entity, this.storeService));
  }

  canDeleteEntityMessages(): string {
    const entity = this.entity;
    if (!entity) return '';

    const deleteRules = this.getDeleteRules();
    if (deleteRules.length === 0) return '';

    return deleteRules
      .filter(rule => !rule.rule(entity, this.storeService))
      .map(rule =>
        rule.errorMessageKey
          ? this.translateService.translate(rule.errorMessageKey)
          : (rule.errorMessage ?? this.translateService.translate('common.errors.cannotDelete')),
      )
      .join('\n');
  }

  private getDeleteRules(): ChoiceListRule[] {
    return this.entityDefinition?.validationRules?.filter(rule => rule.type === BusinessRuleType.DELETE_VALIDATION) ?? [];
  }

  protected deleteConfig() {
    const entity = this.entity;
    const name = (entity && this.entityDefinition?.deleteName?.(entity)) ?? entity?.name ?? '';
    return buildDeleteConfig(this.entityDefinition?.messages?.confirmDelete?.(), name);
  }

  deleteEntityAndClose(): void {
    if (!this.entity) return;

    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<ChoiceList> = { __action: 'delete', entity: this.entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(this.entity);
    }
  }
}
